/**
 * Team colours. Side 0 is the teal family, side 1 the pink family, and every
 * player within a side gets their own tint so partners in a 2v2 are never
 * mistaken for each other. The base tint doubles as the team colour on the
 * scoreboard and in the lobby.
 */
import type { BlobState, Side } from '@office/sim';

export const TEAM_TINTS: readonly [readonly string[], readonly string[]] = [
  ['#4fd1c5', '#8fe3a6', '#5fb3ff'],
  ['#f472b6', '#ff9a6b', '#b48cff'],
];

export const TEAM_NAMES: readonly [string, string] = ['Teal', 'Pink'];

export function teamColor(side: Side): string {
  return TEAM_TINTS[side][0]!;
}

/** Tint for the `index`-th player of a side (0 = the base team colour). */
export function tintFor(side: Side, index: number): string {
  const family = TEAM_TINTS[side];
  return family[Math.min(index, family.length - 1)]!;
}

/** Position of blob `slot` among the blobs on its own side, in slot order. */
export function indexWithinSide(blobs: readonly { side: Side }[], slot: number): number {
  const mine = blobs[slot];
  if (!mine) return 0;
  let index = 0;
  for (let i = 0; i < slot; i++) if (blobs[i]!.side === mine.side) index++;
  return index;
}

/** "Teal", "Teal 2", "Pink 3": how a player is named in the status line. */
export function playerLabel(side: Side, index: number): string {
  return index === 0 ? TEAM_NAMES[side] : `${TEAM_NAMES[side]} ${index + 1}`;
}

export function blobTint(blobs: readonly BlobState[], slot: number): string {
  const blob = blobs[slot];
  return blob ? tintFor(blob.side, indexWithinSide(blobs, slot)) : TEAM_TINTS[0][0]!;
}
